import { useClerk, useUser } from "@clerk/clerk-react";
import { Bug, ChevronsUpDown, LogOut, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import {
  type AdminNavItem,
  adminNavForSectionAndRole,
  adminNavSections,
  adminNavStandaloneForRole,
} from "@/components/admin/adminNav";
import { openSentryBugForm } from "@/components/FloatingHelpButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMobileSidebar } from "@/contexts/MobileSidebarContext";
import { cn } from "@/lib/utils";

export function AdminSidebar() {
  const location = useLocation();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { isOpen, close } = useMobileSidebar();
  const role = user?.publicMetadata?.role as string | undefined;

  const handleNavClick = () => {
    // Close sidebar on mobile when navigating
    close();
  };

  const renderNavLink = (item: AdminNavItem) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    return (
      <Link
        key={item.path}
        to={item.disabled ? "#" : item.path}
        onClick={(e) => {
          if (item.disabled) {
            e.preventDefault();
          } else {
            handleNavClick();
          }
        }}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
          isActive && !item.disabled && "bg-white/10 font-medium text-white",
          !isActive && !item.disabled && "text-white/70 hover:bg-white/5 hover:text-white",
          item.disabled && "cursor-not-allowed text-white/30 opacity-50"
        )}
      >
        <Icon className="h-[18px] w-[18px]" />
        <span>{item.label}</span>
      </Link>
    );
  };

  const userEmail = user?.primaryEmailAddress?.emailAddress ?? "";
  const userName = user?.fullName || user?.firstName || "Admin";
  const userInitial = (userName || userEmail || "A").charAt(0);

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={close}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <div
        className={cn(
          "fixed md:static inset-y-0 left-0 z-50 w-full md:w-64 bg-sidebar text-white h-screen flex flex-col",
          "transform transition-transform duration-300 ease-in-out md:transform-none",
          isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        {/* Logo and Title */}
        <div className="flex items-center justify-between gap-3 border-b border-white/10 p-4">
          <div className="flex items-center gap-3">
            <img src="/dfr-logo.svg" alt="DFR Logo" className="h-8 w-auto" />
            <h1 className="font-semibold text-base">Admin Panel</h1>
          </div>
          {/* X Button - Mobile only */}
          <button
            onClick={close}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/10 transition-colors hover:bg-white/20 md:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Menu */}
        <div className="scrollbar-thin flex-1 overflow-y-auto px-3 py-4">
          {/* Dashboard (standalone, above the grouped sections) */}
          <nav className="space-y-0.5">
            {adminNavStandaloneForRole(role).map((item) => renderNavLink(item))}
          </nav>

          {adminNavSections.map((section) => {
            const items = adminNavForSectionAndRole(section, role);
            if (items.length === 0) {
              return null;
            }
            return (
              <div key={section} className="mt-5">
                <h2 className="mb-1.5 px-3 font-semibold text-white/40 text-[11px] uppercase tracking-wider">
                  {section}
                </h2>
                <nav className="space-y-0.5">{items.map((item) => renderNavLink(item))}</nav>
              </div>
            );
          })}
        </div>

        {/* User context (bottom) */}
        <div className="border-t border-white/10 p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-white/5"
              >
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/10 font-semibold text-sm uppercase">
                  {userInitial}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-sm text-white">{userName}</div>
                  <div className="truncate text-white/50 text-xs">{userEmail}</div>
                </div>
                <ChevronsUpDown className="h-4 w-4 flex-shrink-0 text-white/50" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-[232px]">
              <div className="px-2 py-1.5">
                <div className="truncate font-medium text-sm">{userName}</div>
                <div className="truncate text-muted-foreground text-xs">{userEmail}</div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => openSentryBugForm()}>
                <Bug className="h-4 w-4" />
                Report a bug
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => signOut()}>
                <LogOut className="h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </>
  );
}
